import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; // Asegúrate de usar el hook de navegación

import { verificarToken } from "../services/serviceGlobal"; // Importa la función para validar el token
import TokenEntity from "../core/entities/tokenEntity"; // Importa la entidad TokenEntity
import NotificacionEntity from "../core/entities/notificacionEntity"; // Importa la entidad de notificación (si la tienes)

/*
====================================================================
                      Hook useTokenValidation
====================================================================
Este hook se encarga de manejar la validación de un token de 
autenticación. Utiliza la entidad TokenEntity para encapsular el 
token y proporciona estados como "isValido" y "isValidando".

- Realiza una petición a la API para verificar la validez del token.
- Proporciona un estado para saber si el token es válido.
- Proporciona un estado para saber si la validación está en curso.
- Redirige si el token no es válido.
====================================================================
*/

// ------------------------------------------------------------------
//                   DEFINICIÓN DEL HOOK
// ------------------------------------------------------------------
/**
 * Hook que gestiona la validación de un token de autenticación.
 *
 * @returns {object} Un objeto que contiene el estado del token y la
 *                   función para iniciar la validación.
 */
function useTokenValidation() {
  const navigate = useNavigate(); // Para redirigir al usuario
  const [tokenEntity, setTokenEntity] = useState(new TokenEntity());
  const [errores, setErrores] = useState([]);
  const notificacionRef = useRef(null); // Referencia para la notificación

  // 1. useEffect para obtener el token desde localStorage
  useEffect(() => {
    const token = localStorage.getItem("token"); // Obtén el token del localStorage
    if (token) {
      setTokenEntity(new TokenEntity(token)); // Establece el token en el estado
    } else {
      localStorage.removeItem("token");
      navigate("/"); // Redirige si no hay token en el localStorage
    }
  }, [navigate]);

  // 2. useEffect para validar el token una vez que esté disponible
  useEffect(() => {
    if (tokenEntity.token) {
      iniciarValidacionToken(); // Inicia la validación si hay un token
    }
  }, [tokenEntity.token]);

  // 3. Función para iniciar la validación del token
  const iniciarValidacionToken = async () => {
    setTokenEntity((prev) => {
      return { ...prev, isValidando: true };
    });

    try {
      const response = await verificarToken(tokenEntity.token);
      if (response.ok) {
        var vData=await response.json()
        localStorage.setItem("token", vData.detail.datos.token)
        setTokenEntity((prev) => {
          return {...prev, isValido: true, isValidando: false };
        });
      } else {
        localStorage.removeItem("token");
        navigate("/"); // Redirige si el token no es válido
        setTokenEntity((prev) => {
          return { ...prev, isValido: false, isValidando: false };
        });
        navigate("/"); // Redirige si el token no es válido
      }
    } catch (error) {
      setTokenEntity((prev) => {
        return { ...prev, isValido: false, isValidando: false };
      });
      setErrores([`Error al verificar el token: ${error.message}`]);

      if (notificacionRef.current) {
        notificacionRef.current.show(
          new NotificacionEntity(
            "Error de red",
            "No se pudo conectar con el servidor.",
            "rojo",
            15
          )
        );
      }
    }
  };

  // 4. Función para resetear el token y su estado
  const resetearToken = () => {
    localStorage.removeItem("token"); // Limpia el token del localStorage
    setTokenEntity(new TokenEntity());
    setErrores([]);
  };

  return {
    tokenEntity,
    iniciarValidacionToken,
    resetearToken,
    errores,
    notificacionRef, // Devuelve también la referencia de la notificación
  };
}

export default useTokenValidation;
